import { useDialog } from "@/components/dialog/DialogContext";
import OssDetials from "@/components/hardware/OssDetails";
import ItemContentTabTwo from "@/components/item-content-two/item-content-tab_two";
import { Topic } from "@/components/item-content-two/Topic";
import SimpleItemCard from "@/components/item-content/SimpleItemCard";
import SimpleItemDetails from "@/components/item-content/SimpleItemDetails";
import { useLocale } from "@/contexts/localeContext";
import { TopicContextProvider } from "@/contexts/topic/TopicContextProvider";
import { TranslationKeys } from "@/contexts/TranslationKeys";
import { BreadcrumbLink } from "@/models/BreadcrumbLink";
import { ContentWrapper } from "@/models/ContentWrapper";
import { MultiSelectOption } from "@/models/MultiSelectOption";
import { SimpleItemProfile } from "@/models/SimpleItemProfile";
import { TabSummary } from "@/models/TabSummary";
import { getOssDeviceNamesWithPagination, getOssDevicesById, storeOssDevice } from "@/services/ossDeviceService";
import { getErrorMessage, showLoadItemError } from "@/services/utilities/ServiceError";
import { RouterRootState } from "connected-react-router";
import { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

const OssDevice: FC = () => {
    const getIdFromPath = (path: string) => {
        const splitPath = path.split('/');
        return splitPath[splitPath.length - 1];
    }

    const allItemsPath = '/access-control/hardware';
    const itemPath = '/access-control/hardware/oss-device/';
    const { translate } = useLocale();
    const dialog = useDialog();
    const initialTabs: TabSummary[] = [
        { label: translate.byKey(TranslationKeys.details), badge: 0, showInitial: true, isActive: true },
    ];
    const history = useHistory();
    const pageId = useSelector((state: RouterRootState) => getIdFromPath(state.router.location.pathname));
    const [itemId, setItemId] = useState(pageId);
    const [currentItem, setCurrentItem] = useState<SimpleItemProfile>();
    const [tabs, setTabs] = useState<TabSummary[]>(initialTabs);
    const [breadcrumb, setBreadcrumb] = useState<BreadcrumbLink[]>([]);
    const [busy, setBusy] = useState(false);    
    const [selectedItem, setSelectedItem] = useState<MultiSelectOption | undefined>(undefined);  
    const tabContentList: ContentWrapper[] = [
        {
            content: <OssDetials />,
        },
    ];

    const topic: Topic = {
        accessor: 'ossdName',
        title: translate.byKey(TranslationKeys.ossDevices),
        allItemsPath: allItemsPath,
        itemPath: itemPath
    }

    useEffect(() => {
        setItemId(pageId);
    }, [pageId])

    useEffect(() => {
        reloadPage();
    }, [itemId])

    const getTabContent = (index?: number) => {
        const selectedTabContent = itemId ? tabContentList[index ?? 0] : tabContentList[1];

        return selectedTabContent.content;
    };

    const reloadPage = async () => {
        try {
            let ossDevice;

            if (itemId) {
                ossDevice = await getOssDevicesById(itemId);
                setSelectedItem({ label: ossDevice.name, value: ossDevice.id.toString() })
                setCurrentItem(ossDevice);
            }
            
            setBreadcrumb([
                { value: '/access-control/hardware', displayName: translate.byKey(TranslationKeys.hardware) },
                { value: itemPath + `${itemId}`, displayName: `${ossDevice?.name ?? ''}` }
            ]);
        } catch (error) {
            showLoadItemError(error, dialog, translate, () => { history.push(allItemsPath) });
        }
    }

    const editOssDevice = async (values:any) => {
        setBusy(true);
        storeOssDevice(values)
            .then(async (value: any) => {
                setItemId(value.id)
                history.push(`${itemPath}${value.id}`);
                toast.success(translate.byKey(TranslationKeys.success));
                await reloadPage();
            })
            .catch(failure => { 
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(failure) });
            })
            .finally(() => {
                setBusy(false);
            });
    }

    return <TopicContextProvider
        dataMethod={getOssDeviceNamesWithPagination}
        itemId={itemId}
        topic={topic}
        selectedItem={selectedItem}>
        <ItemContentTabTwo>
            <SimpleItemDetails
                breadcrumb={breadcrumb}
                busy={busy}
                getTabContent={getTabContent}
                itemProfile={currentItem}
                onBack={() => history.push(allItemsPath)}
                saveDetails={editOssDevice}
                tabs={tabs}
                initialValues={currentItem}
                children={<SimpleItemCard />} />
        </ItemContentTabTwo>
    </TopicContextProvider>
}

export default OssDevice;